import { EditableImage } from './EditableImage';
import { GalleryEditorDialog } from './GalleryEditorDialog';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface UniversalImageManagerProps {
  mode: 'single' | 'gallery';
  imageKey?: string;
  fallbackSrc?: string;
  alt?: string;
  category?: string;
  className?: string;
  images?: string[];
  maxSelection?: number;
  allowDelete?: boolean;
  showEditor?: boolean;
  onImageUpdate?: (url: string) => Promise<void>;
  onImageDelete?: () => Promise<void>;
  onImagesUpdate?: (urls: string[]) => Promise<void>;
}

export const UniversalImageManager = ({
  mode,
  imageKey,
  fallbackSrc = '/placeholder.svg',
  alt = 'Image',
  className = '',
  category = 'general',
  images = [],
  allowDelete = true,
  showEditor = true,
  onImageUpdate,
  onImageDelete,
  onImagesUpdate
}: UniversalImageManagerProps) => {
  const [currentImages, setCurrentImages] = useState<string[]>(images);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  // Update local state when images prop changes
  useEffect(() => {
    if (mode === 'gallery') {
      setCurrentImages(images);
    }
  }, [images, mode]);

  const openLightbox = (index: number) => {
    setCurrentImageIndex(index);
    setLightboxOpen(true);
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % currentImages.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + currentImages.length) % currentImages.length);
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!lightboxOpen) return;
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'ArrowLeft') prevImage();
      if (e.key === 'Escape') setLightboxOpen(false);
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, currentImages.length]);

  if (mode === 'gallery') {
    return (
      <>
        <div className="relative">
          {showEditor && (
            <GalleryEditorDialog
              images={currentImages}
              imageKey={imageKey}
              category={category}
              onImagesUpdate={async (newImages) => {
                setCurrentImages(newImages);
                if (onImagesUpdate) {
                  await onImagesUpdate(newImages);
                }
              }}
            />
          )}
          
          {/* Mobile: Carrousel horizontal scrollable */}
          <div className="md:hidden">
            {currentImages.length > 0 ? (
              <div className="relative">
                <div className="flex gap-3 overflow-x-auto snap-x snap-mandatory scrollbar-hide pb-2">
                  {currentImages.map((img, index) => (
                  <div
                    key={index}
                    className="relative flex-shrink-0 w-full cursor-pointer group overflow-hidden rounded-lg snap-start snap-always aspect-[4/3]"
                    onClick={() => openLightbox(index)}
                  >
                    <img
                      src={img || '/placeholder.svg'}
                      alt={`${alt} ${index + 1}`}
                      className="w-full h-full object-cover bg-muted"
                    />
                  </div>
                  ))}
                </div>
                {/* Indicateur de position */}
                <div className="flex justify-center gap-1.5 mt-3">
                  {currentImages.map((_, index) => (
                    <div
                      key={index}
                      className="h-1.5 w-1.5 rounded-full bg-foreground/20"
                    />
                  ))}
                </div>
              </div>
            ) : (
              <div className="aspect-video bg-muted rounded-lg flex items-center justify-center text-muted-foreground">
                Aucune image dans cette galerie
              </div>
            )}
          </div>

          {/* Desktop: Grille */}
          <div className={`hidden md:grid gap-3 ${
            currentImages.length === 1 ? 'md:grid-cols-1' :
            currentImages.length === 2 ? 'md:grid-cols-2' :
            currentImages.length === 4 ? 'md:grid-cols-2' :
            'md:grid-cols-2 lg:grid-cols-3'
          }`}>
            {currentImages.length === 0 ? (
              <div className="col-span-full aspect-video bg-muted rounded-lg flex items-center justify-center text-muted-foreground">
                Aucune image dans cette galerie
              </div>
            ) : currentImages.map((img, index) => {
              const total = currentImages.length;
              // On md (2 cols): last image alone spans 2
              const isLastAloneOn2Cols = total % 2 === 1 && index === total - 1;
              // On lg (3 cols): handle remainders
              const remainderOn3 = total % 3;
              const isLastAloneOn3Cols = remainderOn3 === 1 && index === total - 1;
              const isLastTwoOn3Cols = remainderOn3 === 2 && index >= total - 2;
              
              return (
                <div 
                  key={index} 
                  className={`relative cursor-pointer group overflow-hidden rounded-lg ${
                    isLastAloneOn2Cols ? 'md:col-span-2 md:aspect-[21/9]' : 'md:aspect-[4/3]'
                  } ${
                    isLastAloneOn3Cols ? 'lg:col-span-3 lg:aspect-[21/7]' :
                    isLastTwoOn3Cols ? 'lg:col-span-1 lg:aspect-[4/3]' :
                    ''
                  } ${total <= 4 ? '' : ''}`}
                  onClick={() => openLightbox(index)}
                >
                  <img
                    src={img || '/placeholder.svg'}
                    alt={`${alt} ${index + 1}`}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300" />
                </div>
              );
            })}
          </div>
        </div>

        <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
          <DialogContent className="max-w-[95vw] max-h-[95vh] p-0 bg-black/95 border-none">
            <div className="relative w-full h-[95vh] flex items-center justify-center">
              <img
                src={currentImages[currentImageIndex]}
                alt={`${alt} ${currentImageIndex + 1}`}
                className="max-w-full max-h-full object-contain"
              />
              
              {currentImages.length > 1 && (
                <>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white h-12 w-12 rounded-full"
                    onClick={prevImage}
                  >
                    <ChevronLeft className="h-6 w-6" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white h-12 w-12 rounded-full"
                    onClick={nextImage}
                  >
                    <ChevronRight className="h-6 w-6" />
                  </Button>
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white bg-black/50 px-4 py-2 rounded-full text-sm">
                    {currentImageIndex + 1} / {currentImages.length}
                  </div>
                </>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </>
    );
  }
  
  // Mode single
  return (
    <>
      <div 
        className="cursor-pointer group relative overflow-hidden rounded-2xl"
        onClick={() => {
          setCurrentImages([fallbackSrc]);
          openLightbox(0);
        }}
      >
        <EditableImage
          src={fallbackSrc}
          alt={alt}
          className={className}
          bucket="site-assets"
          path={imageKey ? `${category}/${imageKey}.jpg` : undefined}
          allowDelete={allowDelete}
          onImageUpdate={onImageUpdate}
          onImageDelete={onImageDelete}
        />
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 pointer-events-none" />
      </div>

      <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
        <DialogContent className="max-w-[95vw] max-h-[95vh] p-0 bg-black/95 border-none">
          <div className="relative w-full h-[95vh] flex items-center justify-center">
            <img
              src={fallbackSrc}
              alt={alt}
              className="max-w-full max-h-full object-contain"
            />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
